/**
 * Merge / sanitize AI document-filler results into live formData.
 */

import {
  normalizeCompromissoLoadedFormData,
  normalizeContratoLocacaoLoadedFormData,
  normalizeAutorizacaoLocacaoLoadedFormData,
  normalizeParceriaVendasLoadedFormData,
} from "./compromissoFormDataNormalize";
import { normalizeReciboSimplesLoadedFormData } from "./reciboSimplesFormDataNormalize";

/**
 * @param {unknown} formSchema
 * @returns {Set<string>}
 */
export function collectDocumentFillerTopLevelKeys(formSchema) {
  const keys = new Set();
  if (!Array.isArray(formSchema)) return keys;

  for (const section of formSchema) {
    if (!section || typeof section !== "object") continue;
    if (section.groupKey) keys.add(String(section.groupKey));
    if (Array.isArray(section.fields)) {
      for (const field of section.fields) {
        if (field?.id) keys.add(String(field.id));
      }
    }
    if (section.type === "conditional" && section.options) {
      // fields live under groupKey.fields — do not allow flat option fields at root
    }
  }
  return keys;
}

/**
 * Drop flat conditional field leaks and keep only schema top-level keys.
 *
 * @param {Record<string, unknown>} result
 * @param {unknown} formSchema
 */
export function sanitizeDocumentFillerResult(result, formSchema) {
  if (!result || typeof result !== "object" || Array.isArray(result)) {
    return {};
  }
  const allowed = collectDocumentFillerTopLevelKeys(formSchema);
  if (allowed.size === 0) return { ...result };

  const cleaned = {};
  for (const [key, value] of Object.entries(result)) {
    if (allowed.has(key)) cleaned[key] = value;
  }
  return cleaned;
}

function mergeConditional(prevValue, nextValue) {
  const prev =
    prevValue && typeof prevValue === "object" && !Array.isArray(prevValue)
      ? prevValue
      : {};
  const next =
    nextValue && typeof nextValue === "object" && !Array.isArray(nextValue)
      ? nextValue
      : {};
  return {
    type: next.type != null && next.type !== "" ? next.type : prev.type ?? "",
    fields: {
      ...(prev.fields && typeof prev.fields === "object" ? prev.fields : {}),
      ...(next.fields && typeof next.fields === "object" ? next.fields : {}),
    },
  };
}

/**
 * @param {Record<string, unknown>} prev
 * @param {Record<string, unknown>} result
 * @param {unknown} formSchema
 */
export function deepMergeDocumentFillerResult(prev, result, formSchema) {
  const base =
    prev && typeof prev === "object" && !Array.isArray(prev) ? { ...prev } : {};
  const sanitized = sanitizeDocumentFillerResult(result, formSchema);
  const conditionalKeys = new Set();
  if (Array.isArray(formSchema)) {
    for (const section of formSchema) {
      if (section?.type === "conditional" && section.groupKey) {
        conditionalKeys.add(String(section.groupKey));
      }
    }
  }

  for (const [key, value] of Object.entries(sanitized)) {
    if (conditionalKeys.has(key)) {
      base[key] = mergeConditional(base[key], value);
      continue;
    }
    if (Array.isArray(value)) {
      base[key] = value;
      continue;
    }
    base[key] = value;
  }
  return base;
}

function normalizeByTemplateKey(templateKey, data) {
  const key = String(templateKey || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

  if (
    key === "compromisso-compra-venda" ||
    key === "proposta-compra-imovel"
  ) {
    return normalizeCompromissoLoadedFormData(data);
  }
  if (key === "contrato-parceria-vendas") {
    return normalizeParceriaVendasLoadedFormData(data);
  }
  if (key === "contrato-locacao-imovel") {
    return normalizeContratoLocacaoLoadedFormData(data);
  }
  if (
    key === "autorizacao-locacao-imovel" ||
    key === "autorizacao-gleba-loteamento" ||
    key === "autorizacao-venda-imovel"
  ) {
    return normalizeAutorizacaoLocacaoLoadedFormData(data);
  }
  if (key === "recibo-simples") {
    return normalizeReciboSimplesLoadedFormData(data);
  }
  return data;
}

/**
 * @param {Record<string, unknown>} prev
 * @param {Record<string, unknown>} result
 * @param {{ formSchema?: unknown, templateKey?: string }} [options]
 */
export function applyDocumentFillerResult(prev, result, options = {}) {
  const { formSchema, templateKey } = options;
  const merged = deepMergeDocumentFillerResult(prev, result, formSchema);
  return normalizeByTemplateKey(templateKey, merged);
}

export default applyDocumentFillerResult;
